/* eslint-disable no-empty-pattern */
import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';

// Utils
import http from 'utils/http';
import { isNotNil } from 'feature/loading-management/utils/helpers';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import { decodeURI } from 'utils/helpers/url';
import { SUPPLIER_AUTH_FILTERS } from 'utils/constants';
import { setFilters } from 'feature/loading-management/filters/store/filtersSlice';
import { createSnack } from 'snackbar/snackbarSlice';
import { ANY_VALUE } from '../../utils/const';

const initialState = {
  activeMyAuthTab: 'baseProducts',
  filtersOpen: false,
  myAuthorizationCards: {},
  myAuthorizationConfirmationDialog: {},
  myAuthorizationPending: true,
  selectorsList: [],
  selectorsListPending: true,
};

const reducers = {
  resetAuthSequencing: R.always(initialState),
  setActiveMyAuthTab: R.assoc('activeMyAuthTab'),
  setFiltersOpen: R.assoc('filtersOpen'),
  setMyAuthorizationCards: R.assoc('myAuthorizationCards'),
  setMyAuthorizationConfirmationDialog: R.assoc('myAuthorizationConfirmationDialog'),
  setMyAuthorizationPending: R.assoc('myAuthorizationPending'),
  setSelectorsList: R.assoc('selectorsList'),
  setSelectorsListPending: R.assoc('selectorsListPending'),
  updateTwoPartyExchange: (
    { activeMyAuthTab, exchange, productAuthorizationId, viewBy },
    state
  ) => {
    const isProduct = viewBy === 'product';
    const isAccept = exchange === 'accept';
    // if accepting you want exchange confirmed to be true
    // if rejecting you want exchange object to be null
    const value = isAccept ? { confirmed: true } : null;
    const cardPath = isProduct
      ? ['myAuthorizationCards']
      : ['myAuthorizationCards', activeMyAuthTab];
    const currentCardList = R.path(cardPath, state);
    const index = currentCardList.findIndex(
      el => el.productAuthorizationId === productAuthorizationId
    );
    const cardToUpdatePath = [...cardPath, index, 'exchange'];
    return R.assocPath(cardToUpdatePath, value, state);
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'myAuthorizations',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const {
  resetAuthSequencing,
  setActiveMyAuthTab,
  setMyAuthorizationCards,
  setMyAuthorizationConfirmationDialog,
  setMyAuthorizationPending,
  setFiltersOpen,
  setSelectorsList,
  setSelectorsListPending,
  updateTwoPartyExchange,
} = actions;

// Selectors
export const getMyAuthItem = item => R.path(['loadingManagement', 'myAuthorizations', item]);

export const getSelectorsData = createSelector(
  R.path(['loadingManagement', 'filters', 'searchTerm']),
  getMyAuthItem('selectorsList'),
  (searchTerm, dataList) => {
    if (searchTerm) {
      return dataList.filter(({ nameShort }) =>
        nameShort.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return dataList;
  }
);

export const getCurrentSelection = createSelector(
  getSelectorsData,
  R.path(['loadingManagement', 'filters', 'selectedIndex']),
  (selectorsList, selectedIndex) => selectorsList[selectedIndex] || {}
);

// helper function to return cards to filter from array or object with current tab
export const getAllCards = createSelector(
  getMyAuthItem('myAuthorizationCards'),
  getMyAuthItem('activeMyAuthTab'),
  (array, tabName) => R.propOr(array, tabName, array)
);

const exchangeFilterTransformation = R.cond([
  [R.equals(SUPPLIER_AUTH_FILTERS.turnedOn), () => R.path(['exchange', 'confirmed'])],
  [R.equals(SUPPLIER_AUTH_FILTERS.turnedOff), () => R.o(R.isNil, R.prop('exchange'))],
  [
    R.equals(SUPPLIER_AUTH_FILTERS.pendingAcceptance),
    () => R.pathEq(['exchange', 'confirmed'], false),
  ],
]);

const typeFilterTransformation = R.cond([
  [R.equals(SUPPLIER_AUTH_FILTERS.unlimited), () => R.prop('unlimited')],
  [
    R.equals(SUPPLIER_AUTH_FILTERS.limited),
    () => R.o(R.equals(R.toUpper(SUPPLIER_AUTH_FILTERS.limited)), R.prop('limitType')),
  ],
  [
    R.equals(SUPPLIER_AUTH_FILTERS.monthly),
    () => R.o(R.equals(R.toUpper(SUPPLIER_AUTH_FILTERS.monthly)), R.prop('limitType')),
  ],
  [
    R.equals(SUPPLIER_AUTH_FILTERS.semiMonthly),
    () => R.o(R.equals(R.toUpper(SUPPLIER_AUTH_FILTERS.semiMonthly)), R.prop('limitType')),
  ],
  [
    R.equals(SUPPLIER_AUTH_FILTERS.weekly),
    () => R.o(R.equals(R.toUpper(SUPPLIER_AUTH_FILTERS.weekly)), R.prop('limitType')),
  ],
  [
    R.equals(SUPPLIER_AUTH_FILTERS.daily),
    () => R.o(R.equals(R.toUpper(SUPPLIER_AUTH_FILTERS.daily)), R.prop('limitType')),
  ],
]);

const statusFilterTransformation = R.cond([
  [R.equals(SUPPLIER_AUTH_FILTERS.turnedOn), () => R.prop('active')],
  [R.equals(SUPPLIER_AUTH_FILTERS.turnedOff), () => R.complement(R.prop('active'))],
  [
    R.equals(SUPPLIER_AUTH_FILTERS.limitMet),
    () => R.both(R.o(R.defaultTo(true), R.prop('limitMet')), R.propEq('unlimited', false)),
  ],
]);

const stateFilterTransformation = R.pathEq(['location', 'state']);
const locationFilterTransformation = R.pathEq(['location', 'nameShort']);
const productFilterTransformation = R.pathEq(['product', 'nameShort']);
const inventoryOwnerFilterTransformation = R.pathEq(['inventoryOwner', 'nameShort']);

const filterTransformations = {
  active: statusFilterTransformation,
  exchange: exchangeFilterTransformation,
  inventoryOwner: inventoryOwnerFilterTransformation,
  limitType: typeFilterTransformation,
  location: locationFilterTransformation,
  product: productFilterTransformation,
  state: stateFilterTransformation,
};

export const getApplicableFilters = R.o(
  R.pick(['active', 'exchange', 'inventoryOwner', 'limitType', 'location', 'product', 'state']),
  R.path(['loadingManagement', 'filters'])
);

const getFilters = R.compose(
  // array of predicates and returns true if all evaluate to true
  R.allPass,
  // returns array of functions from object
  R.values,
  // transform the loadingManagement filter to match data
  R.evolve(filterTransformations),
  // remove any filters that do not have a value chosen
  R.filter(R.o(R.not, R.equals(ANY_VALUE))),
  // only grab filters applicable to My Authorizations
  getApplicableFilters
);

// getCardsToDisplay :: object, object -> array
export const getCardsToDisplay = createSelector(getFilters, getAllCards, R.filter);

// Actions
export const fetchUniqueSelectors = (queryParams, type, _, cancelToken) => dispatch => {
  dispatch(setSelectorsListPending(true));
  dispatch(setMyAuthorizationPending(true));
  dispatch(setSelectorsList([]));
  dispatch(setMyAuthorizationCards([]));
  dispatch(setFilters({ name: 'selectedIndex', value: undefined }));
  const paramString = getStringifiedQueryParams(queryParams);

  const URL = `supplier-authorizations/get-unique-my-${type}s?${paramString}`;
  // return the promise data so that we can see if
  // data was returned for the sidebar to determine
  // if the cards panel needs to render the no available
  // card or a list of cards
  return http
    .get(URL, { cancelToken })
    .then(({ data }) => {
      data && dispatch(setSelectorsList(data));
      return data;
    })
    .finally(() => dispatch(setSelectorsListPending(false)));
};

export const updateTabForGoToMyAuth = (product, inputData) => (dispatch, getState) => {
  // determine the correct active tab for initial render
  // product is set in MyAuthorization component
  // setting tab here allows us to not render the wrong set of cards
  if (product !== ANY_VALUE) {
    const data = inputData ?? getMyAuthItem('myAuthorizationCards')(getState());
    // check to see if we should have custom tab
    const isCustom = R.compose(
      isNotNil,
      R.find(R.pathEq(['product', 'nameShort'], product)),
      R.prop('customProducts')
    )(data);

    // set appropriate tab
    dispatch(setActiveMyAuthTab(isCustom ? 'customProducts' : 'baseProducts'));

    // I would have ultimately liked to reset the product filter if neither the baseProducts or customProducts lists contain the product, but that seems overly defensive
    // If that happens, it is likely a BE issue
  }
};

export const fetchMyAuthorizations = (queryParams, viewBy, cancelToken) => (dispatch, getState) => {
  const paramString = getStringifiedQueryParams(queryParams);
  dispatch(setMyAuthorizationPending(true));

  const URL = `supplier-authorizations/view-my-${viewBy}-authorizations?${paramString}`;

  http.get(URL, { cancelToken }).then(({ data }) => {
    // need to check to make sure there is data
    // if the cancel token is fired, data will be undefined and app will crash
    if (data) {
      const { product } = getApplicableFilters(getState());
      // if navigating using notifications, need to check which tab to default to
      dispatch(updateTabForGoToMyAuth(product, data));
      // set the cards after setting the proper tab
      dispatch(setMyAuthorizationCards(data));

      // need to reset pending state only if data is returned
      // if this is not here, then we clear skeletons if there is an error returning data
      // this causes cards from a previous selector to be shown for the selector that errored
      dispatch(setMyAuthorizationPending(false));
    }
  });
};

export const toggleTwoPartyExchange = cancelToken => (dispatch, getState) => {
  const {
    activeMyAuthTab,
    myAuthorizationConfirmationDialog: { params },
  } = getState().loadingManagement.myAuthorizations;
  const { productAuthorizationId, twoPartyExchangeInfo, type, viewBy, viewById } = params;
  http
    .post(`supplier-authorizations/${type}-exchange`, { productAuthorizationId }, { cancelToken })
    .then(() =>
      dispatch(
        updateTwoPartyExchange({
          activeMyAuthTab,
          exchange: type,
          productAuthorizationId,
          viewBy,
        })
      )
    )
    .finally(() => {
      dispatch(setMyAuthorizationConfirmationDialog({}));
      dispatch(
        createSnack({
          message: `Two Party Exchange for ${twoPartyExchangeInfo.title} loading from ${
            twoPartyExchangeInfo.subtitle
          } has been ${type}ed at ${decodeURI(viewById)}.`,
        })
      );
    });
};

export default reducer;
